import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { HttpError, request } from "../../../api/http";
import { useConfirm, usePrompt } from "../../../components/Dialog";
import { useAuth } from "../../../context/AuthContext";
import { UnpaidInvoiceRow } from "../../../types/impagos";
import PaymentMethodsDrawer from "../../SubscriptionDetail/components/PaymentMethodsDrawer";

const STRIPE_DASHBOARD = "https://dashboard.stripe.com/customers/";

type Busy =
  | null
  | "complete"
  | "reassign"
  | "regenerate"
  | "conciliate"
  | "status";

const todayISO = () => new Date().toISOString().slice(0, 10);

type Props = {
  row: UnpaidInvoiceRow;
  onReload: () => void;
};

export default function ImpagoActionsMenu({ row, onReload }: Props) {
  const { token } = useAuth();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<Busy>(null);
  const [feedback, setFeedback] = useState<
    | { kind: "success" | "error" | "info"; message: string }
    | null
  >(null);
  const [pmDrawerOpen, setPmDrawerOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const conciliateAllowed =
    row.leadMarket === "IT" || row.leadMarket === "SQL_ES";

  const post = async (
    path: string,
    body: Record<string, unknown> | undefined,
    busyKey: Busy,
    successMsg: string,
  ) => {
    if (busy) return;
    setBusy(busyKey);
    setFeedback(null);
    try {
      await request(path, { method: "POST", token, body: body ?? {} });
      setFeedback({ kind: "success", message: successMsg });
      onReload();
    } catch (err) {
      setFeedback({
        kind: "error",
        message: err instanceof HttpError
          ? `HTTP ${err.status}: ${
              ((err.body as { message?: string })?.message ?? "").slice(0, 200)
            }`
          : "Acción falló",
      });
    } finally {
      setBusy(null);
    }
  };

  const handleComplete = async () => {
    const date = await prompt({
      title: "Marcar factura como pagada",
      message: `Factura ${row.invoiceId}. Introduce la fecha de pago.`,
      defaultValue: todayISO(),
      type: "date",
      okLabel: "Marcar como pagada",
      validate: (v) =>
        /^\d{4}-\d{2}-\d{2}$/.test(v) ? null : "Formato esperado: YYYY-MM-DD",
    });
    if (!date) return;
    await post(
      `/api/admin/operations/invoices/${row.invoiceId}/complete`,
      { completionDate: date },
      "complete",
      "Pago completado",
    );
  };

  const handleRegenerate = async () => {
    const ok = await confirm({
      title: "Regenerar factura Stripe",
      message: "¿Regenerar nueva factura en Stripe? Esto creará un nuevo charge.",
      confirmLabel: "Regenerar",
      tone: "warning",
    });
    if (!ok) return;
    await post(
      `/api/admin/operations/invoices/${row.invoiceId}/regenerate-stripe`,
      undefined,
      "regenerate",
      "Nueva factura Stripe generada",
    );
  };

  const handleReassign = async () => {
    const stripeId = await prompt({
      title: "Reasignar Stripe invoice",
      message: `Factura ${row.invoiceId}. Introduce el Stripe invoice ID al que reasignarla.`,
      placeholder: "in_...",
      okLabel: "Reasignar",
      tone: "warning",
      validate: (v) => (v.startsWith("in_") ? null : "Debe empezar por 'in_'"),
    });
    if (!stripeId) return;
    await post(
      `/api/admin/operations/invoices/${row.invoiceId}/reassign-stripe`,
      { stripeInvoiceId: stripeId },
      "reassign",
      "Stripe invoice reasignado",
    );
  };

  const handleConciliate = async () => {
    if (!conciliateAllowed) return;
    const ok = await confirm({
      title: "Enviar a conciliación",
      message: `Enviar la factura a conciliación AGL/SquareLife (market ${row.leadMarket}).`,
      confirmLabel: "Enviar a conciliación",
    });
    if (!ok) return;
    await post(
      `/api/admin/operations/invoices/${row.invoiceId}/conciliate`,
      undefined,
      "conciliate",
      "Enviada a conciliación",
    );
  };

  const handleCheckStatus = async () => {
    if (busy) return;
    setBusy("status");
    setFeedback(null);
    try {
      const data = await request<{ invoiceId: string; status: unknown }>(
        `/api/admin/operations/invoices/${row.invoiceId}/conciliation-status`,
        { token },
      );
      setFeedback({
        kind: "info",
        message: `Status: ${typeof data.status === "string" ? data.status : JSON.stringify(data.status)}`,
      });
    } catch (err) {
      setFeedback({
        kind: "error",
        message: err instanceof HttpError ? `HTTP ${err.status}` : "Falló",
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      ref={wrapperRef}
      style={{ position: "relative", display: "inline-block" }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Acciones"
        style={{
          padding: "0.3rem 0.6rem",
          borderRadius: "0.35rem",
          background: open ? "#0069d9" : "white",
          color: open ? "white" : "#1f2933",
          border: "1px solid #dee2e6",
          fontSize: ".75rem",
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        ⋯
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            zIndex: 25,
            background: "white",
            border: "1px solid #dee2e6",
            borderRadius: "0.5rem",
            boxShadow: "0 6px 16px rgba(0,0,0,.12)",
            minWidth: 260,
            padding: "0.35rem",
          }}
        >
          <SectionLabel>Pagar / Recobrar</SectionLabel>
          <MenuItem
            onClick={() => {
              setOpen(false);
              handleComplete();
            }}
            disabled={busy === "complete"}
          >
            Marcar como pagada…
          </MenuItem>
          <MenuItem
            onClick={() => {
              setOpen(false);
              handleRegenerate();
            }}
            disabled={busy === "regenerate"}
          >
            Regenerar factura Stripe
          </MenuItem>
          <MenuItem
            onClick={() => {
              setOpen(false);
              handleReassign();
            }}
            disabled={busy === "reassign"}
          >
            Reasignar Stripe invoice…
          </MenuItem>
          <MenuItem
            onClick={() => {
              setOpen(false);
              setPmDrawerOpen(true);
            }}
            disabled={!row.subscriptionId}
            hint={!row.subscriptionId ? "Factura sin suscripción asociada" : undefined}
          >
            Cambiar método de pago default…
          </MenuItem>

          {conciliateAllowed && (
            <>
              <div style={dividerStyle} />
              <SectionLabel>Conciliación carrier</SectionLabel>
              <MenuItem
                onClick={() => {
                  setOpen(false);
                  handleConciliate();
                }}
                disabled={busy === "conciliate"}
              >
                Enviar a conciliación
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setOpen(false);
                  handleCheckStatus();
                }}
                disabled={busy === "status"}
              >
                Ver status conciliación
              </MenuItem>
            </>
          )}

          <div style={dividerStyle} />
          <SectionLabel>Navegación</SectionLabel>
          {row.leadUlid && (
            <MenuLink to={`/leads/${row.leadUlid}`}>Abrir lead</MenuLink>
          )}
          {row.subscriptionId && (
            <MenuLink to={`/subscriptions/${row.subscriptionId}`}>
              Abrir suscripción
            </MenuLink>
          )}
          {row.stripeCustomerId && (
            <MenuExternal href={`${STRIPE_DASHBOARD}${row.stripeCustomerId}`}>
              Abrir cliente en Stripe
            </MenuExternal>
          )}
        </div>
      )}
      {feedback && (
        <div
          onClick={() => setFeedback(null)}
          title="Cerrar"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            zIndex: 30,
            background:
              feedback.kind === "success"
                ? "#d4edda"
                : feedback.kind === "info"
                  ? "#e7f1ff"
                  : "#fde2e2",
            color:
              feedback.kind === "success"
                ? "#155724"
                : feedback.kind === "info"
                  ? "#0a58ca"
                  : "#b32424",
            padding: "0.4rem 0.65rem",
            borderRadius: "0.4rem",
            fontSize: ".75rem",
            maxWidth: 280,
            boxShadow: "0 4px 10px rgba(0,0,0,.1)",
            cursor: "pointer",
          }}
        >
          {feedback.message}
        </div>
      )}
      {pmDrawerOpen && row.subscriptionId && (
        <PaymentMethodsDrawer
          subscriptionId={row.subscriptionId}
          leadUlid={row.leadUlid}
          currentDefault={row.defaultPaymentMethod ?? ""}
          onClose={() => setPmDrawerOpen(false)}
          onChanged={onReload}
        />
      )}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: "0.3rem 0.55rem 0.2rem",
        fontSize: ".65rem",
        color: "#73879C",
        textTransform: "uppercase",
        letterSpacing: ".4px",
        fontWeight: 700,
      }}
    >
      {children}
    </div>
  );
}

function MenuItem({
  onClick,
  disabled,
  hint,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={hint}
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        padding: "0.4rem 0.55rem",
        borderRadius: "0.35rem",
        background: "transparent",
        color: disabled ? "#adb5bd" : "#1f2933",
        border: "none",
        fontSize: ".82rem",
        fontWeight: 500,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.background = "#f8f9fb";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      {children}
    </button>
  );
}

function MenuLink({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        padding: "0.4rem 0.55rem",
        borderRadius: "0.35rem",
        color: "#0069d9",
        textDecoration: "none",
        fontSize: ".82rem",
        fontWeight: 500,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#f8f9fb";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      {children}
    </Link>
  );
}

function MenuExternal({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        padding: "0.4rem 0.55rem",
        borderRadius: "0.35rem",
        color: "#0069d9",
        textDecoration: "none",
        fontSize: ".82rem",
        fontWeight: 500,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#f8f9fb";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      {children} ↗
    </a>
  );
}

const dividerStyle: React.CSSProperties = {
  borderTop: "1px solid #f1f3f5",
  margin: "0.25rem 0",
};
